import cx from 'classnames';
import * as React from 'react';
import type { AnyIcon } from '../icon/Icon';
import { Icon } from '../icon/Icon';
import { Tooltip } from '../tooltip/Tooltip';
import styles from './IconButton.scss';

export type IconButtonProps = {
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  iconClassName?: string;
  iconFill?: string;
  iconName: AnyIcon;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  testId?: string;
  showLabel?: boolean;
  /** With `dataTooltipId`, wraps the button in the shared Tooltip. */
  tooltipContent?: string;
  /** Built-in tooltip: use with `tooltipContent`. External tooltip: optional alone. */
  dataTooltipId?: string;
  place?: 'top' | 'bottom' | 'left' | 'right';
  /** Use `fixed` when the button sits inside `overflow: hidden` (e.g. input end adornment). */
  tooltipPositionStrategy?: 'absolute' | 'fixed';
};

export const IconButton = (props: IconButtonProps) => {
  const {
    ariaLabel,
    className,
    disabled = false,
    iconClassName,
    iconFill,
    iconName,
    onClick,
    testId = '',
    showLabel = false,
  } = props;

  const builtInTooltip =
    typeof props.tooltipContent === 'string' && props.dataTooltipId
      ? {
          content: props.tooltipContent,
          id: props.dataTooltipId,
        }
      : null;

  const button = (
    <button
      aria-label={ariaLabel}
      className={cx(styles.button, className)}
      data-tooltip-id={builtInTooltip ? undefined : props.dataTooltipId}
      disabled={disabled}
      onClick={onClick}
      title={builtInTooltip || props.dataTooltipId ? undefined : ariaLabel}
      type="button"
    >
      <Icon
        className={iconClassName}
        fill={iconFill}
        name={iconName}
        testId={testId}
      />
      {showLabel && ariaLabel}
    </button>
  );

  if (builtInTooltip) {
    return (
      <Tooltip
        content={builtInTooltip.content}
        id={builtInTooltip.id}
        positionStrategy={props.tooltipPositionStrategy}
        tooltipClassName={styles.iconTooltip}
        place={props.place ?? 'top'}
      >
        {button}
      </Tooltip>
    );
  }

  return button;
};
